import clsx from 'clsx';
import React, { useEffect, useRef } from 'react';
import { Icon } from 'shared/components/Icon';
import { useAppSelector } from 'shared/store';
import { selectOutgoingCameraStream } from 'shared/store/realtime-slice';
import log from 'shared/utils/log';
import './style.less';

export const ActiveCallSelfView: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const camStreamOut = useAppSelector(selectOutgoingCameraStream);
  const callStatus = useAppSelector(s => s.realtime.callStatus);
  const { isOutgoingAudioEnabled, isOutgoingVideoEnabled } = useAppSelector(
    s => s.dashboard,
  );

  useEffect(() => {
    if (!videoRef.current) return;
    if (!camStreamOut) return;
    if (!isOutgoingVideoEnabled) return;

    videoRef.current.muted = true;
    videoRef.current.srcObject = camStreamOut;

    videoRef.current.play().catch(error => {
      log.log(`There was an error trying to play the ActiveCallSelfView`, error);
    });
  }, [camStreamOut, isOutgoingVideoEnabled]);

  useEffect(() => {
    if (!camStreamOut || !isOutgoingAudioEnabled) return;
    try {
      const audioContext = new AudioContext();
      const audioSource = audioContext.createMediaStreamSource(camStreamOut);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;
      analyser.minDecibels = -127;
      analyser.maxDecibels = 0;
      analyser.smoothingTimeConstant = 0.4;
      audioSource.connect(analyser);
      const volumes = new Uint8Array(analyser.frequencyBinCount);
      const volumeCallback = () => {
        const dots = document.querySelectorAll<HTMLDivElement>(
          '#ActiveCallSelfView__mic-dot',
        );
        if (!dots || dots.length === 0) return;
        analyser.getByteFrequencyData(volumes);
        let volumeSum = 0;
        for (const volume of volumes) volumeSum += volume;
        const averageVolume = volumeSum / volumes.length;
        const volumePercent = (averageVolume / 127) * 100;
        const MIN_HEIGHT = 3;
        const MAX_HEIGHT = 8;
        const DELTA = MAX_HEIGHT - MIN_HEIGHT;
        dots.forEach(dot => {
          dot.style.height = `${
            Math.min((volumePercent * 2) / 100, 1) * DELTA + MIN_HEIGHT
          }px`;
        });
        requestAnimationFrame(volumeCallback);
      };
      volumeCallback();
    } catch (error) {
      //
    }
  }, [camStreamOut, isOutgoingAudioEnabled]);

  return (
    <div
      className={clsx(
        'ActiveCallSelfView',
        camStreamOut &&
          isOutgoingVideoEnabled &&
          callStatus !== 'off' &&
          'ActiveCallSelfView--enabled',
      )}
    >
      <video ref={videoRef} />
      {camStreamOut && isOutgoingVideoEnabled && callStatus !== 'off' && (
        <div className="ActiveCallSelfView__mic">
          {!isOutgoingAudioEnabled ? (
            <Icon icon="micOffOutline" />
          ) : (
            <>
              <Icon icon="micOnOutline" />
              <div
                id="ActiveCallSelfView__mic-dot"
                className="ActiveCallSelfView__mic-dot"
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};
